import React, { useState, Fragment } from 'react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon, FilterIcon, BoxIcon, UtensilsIcon, Loader2Icon, ScissorsIcon, PackageIcon, ArrowUpFromLineIcon, CheckIcon, AlertTriangleIcon } from 'lucide-react';
interface Material {
  name: string;
  batchNumber: string;
  quantity: string;
  unit: string;
}
interface ProductionStep {
  step: 'kutterei' | 'fuellerei' | 'raeucherei' | 'schaelerei' | 'verpackung';
  status: 'completed' | 'in_progress' | 'pending';
  startTime?: string;
  completionTime?: string;
  batchNumber: string;
  materials: Material[];
  operator?: string;
}
interface BatchRecord {
  id: string;
  batchNumber: string;
  productName: string;
  productionDate: string;
  exported: boolean;
  exportedAt?: string;
  recipe: {
    name: string;
    batchNumber: string;
    ingredients: Material[];
  };
  productionSteps: ProductionStep[];
}
const mockBatchRecords: BatchRecord[] = [{
  id: '1',
  batchNumber: '130901230425',
  productName: 'Pfefferbeißer Pikante LIDL QS ITW',
  productionDate: '2024-01-20',
  exported: false,
  recipe: {
    name: 'Pfefferbeißer Rezept',
    batchNumber: 'R001-240120',
    ingredients: [{
      name: 'Schweineschulter',
      batchNumber: '116425',
      quantity: '579,5',
      unit: 'kg'
    }, {
      name: 'Maturation Starter',
      batchNumber: '4414763178',
      quantity: '250',
      unit: 'kg'
    }, {
      name: 'Gewürzmischung Pfefferbeisserpikant',
      batchNumber: '20250415-38708',
      quantity: '4,88',
      unit: 'kg'
    }]
  },
  productionSteps: [{
    step: 'kutterei',
    status: 'completed',
    startTime: '2024-01-20T08:00:00',
    completionTime: '2024-01-20T09:30:00',
    batchNumber: 'KUT-2024-0001',
    operator: 'K. Schmidt',
    materials: [{
      name: 'Schweineschulter',
      batchNumber: '116425',
      quantity: '579,5',
      unit: 'kg'
    }, {
      name: 'Maturation Starter',
      batchNumber: '4414763178',
      quantity: '250',
      unit: 'kg'
    }]
  }, {
    step: 'fuellerei',
    status: 'completed',
    startTime: '2024-01-20T09:45:00',
    completionTime: '2024-01-20T11:00:00',
    batchNumber: 'FUE-2024-0001',
    operator: 'M. Weber',
    materials: [{
      name: 'Naturdarm 28/30',
      batchNumber: 'DA-2024-0089',
      quantity: '10',
      unit: 'Bund'
    }]
  }]
}, {
  id: '2',
  batchNumber: '130901230426',
  productName: 'Salametti Classic EDEKA',
  productionDate: '2024-01-21',
  exported: true,
  exportedAt: '2024-01-21T15:30:00',
  recipe: {
    name: 'Salametti Rezept',
    batchNumber: 'R002-240121',
    ingredients: [{
      name: 'Schweinefleisch',
      batchNumber: '116426',
      quantity: '450',
      unit: 'kg'
    }, {
      name: 'Gewürzmischung Classic',
      batchNumber: '20250415-38709',
      quantity: '3.5',
      unit: 'kg'
    }]
  },
  productionSteps: [{
    step: 'kutterei',
    status: 'completed',
    startTime: '2024-01-21T08:00:00',
    completionTime: '2024-01-21T09:00:00',
    batchNumber: 'KUT-2024-0002',
    operator: 'K. Schmidt',
    materials: [{
      name: 'Schweinefleisch',
      batchNumber: '116426',
      quantity: '450',
      unit: 'kg'
    }]
  }]
}, {
  id: '3',
  batchNumber: '130901230427',
  productName: 'Landjäger Premium REWE',
  productionDate: '2024-01-22',
  exported: false,
  recipe: {
    name: 'Landjäger Rezept',
    batchNumber: 'R003-240122',
    ingredients: [{
      name: 'Rindfleisch',
      batchNumber: '116427',
      quantity: '300',
      unit: 'kg'
    }, {
      name: 'Gewürzmischung Premium',
      batchNumber: '20250415-38710',
      quantity: '2.5',
      unit: 'kg'
    }]
  },
  productionSteps: [{
    step: 'kutterei',
    status: 'completed',
    startTime: '2024-01-22T08:00:00',
    completionTime: '2024-01-22T09:00:00',
    batchNumber: 'KUT-2024-0003',
    operator: 'K. Schmidt',
    materials: [{
      name: 'Rindfleisch',
      batchNumber: '116427',
      quantity: '300',
      unit: 'kg'
    }]
  }]
}];
const productionStepIcons = {
  kutterei: BoxIcon,
  fuellerei: UtensilsIcon,
  raeucherei: Loader2Icon,
  schaelerei: ScissorsIcon,
  verpackung: PackageIcon
};
const productionStepLabels = {
  kutterei: 'Kutterei',
  fuellerei: 'Füllerei',
  raeucherei: 'Räucherei',
  schaelerei: 'Schälerei',
  verpackung: 'Verpackung'
};
export function BTraceExport() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState('');
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [records, setRecords] = useState(mockBatchRecords);
  const toggleRow = (id: string) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  const toggleSelectRecord = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedRecords(prev => prev.includes(id) ? prev.filter(recordId => recordId !== id) : [...prev, id]);
  };
  const toggleSelectAll = (records: BatchRecord[]) => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(record => record.id));
    }
  };
  const handleExport = () => {
    const now = new Date().toISOString();
    setRecords(prevRecords => prevRecords.map(record => {
      if (selectedRecords.includes(record.id)) {
        return {
          ...record,
          exported: true,
          exportedAt: now
        };
      }
      return record;
    }));
    setSelectedRecords([]);
  };
  const filteredAndSortedRecords = records.filter(record => {
    const matchesSearch = record.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) || record.productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = dateFilter ? record.productionDate === dateFilter : true;
    return matchesSearch && matchesDate;
  }).sort((a, b) => {
    if (a.exported !== b.exported) {
      return a.exported ? 1 : -1;
    }
    return new Date(b.productionDate).getTime() - new Date(a.productionDate).getTime();
  });
  return <div className="p-6 h-full" data-id="element-13">
      <header className="mb-8" data-id="element-14">
        <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-15">B-Trace Export</h1>
        <p className="text-gray-600" data-id="element-16">
          Exportieren Sie Ihre Chargeninformationen für B-Trace
        </p>
      </header>
      <div className="bg-white rounded-lg shadow" data-id="element-17">
        <div className="p-4 border-b border-gray-200" data-id="element-18">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-19">
            <div className="relative" data-id="element-20">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-21" />
              <input type="text" placeholder="Charge oder Produkt suchen..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-22" />
            </div>
            <div className="relative" data-id="element-23">
              <input type="date" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={dateFilter} onChange={e => setDateFilter(e.target.value)} data-id="element-24" />
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-25" />
            </div>
            <button onClick={handleExport} className="flex items-center justify-center gap-2 px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedRecords.length === 0} data-id="element-26">
              <DownloadIcon className="h-5 w-5" data-id="element-27" />
              <span data-id="element-28">
                {selectedRecords.length > 0 ? `${selectedRecords.length} Datensätze exportieren` : 'Export starten'}
              </span>
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-29">
          <table className="w-full" data-id="element-30">
            <thead className="bg-gray-50" data-id="element-31">
              <tr data-id="element-32">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-33">
                  <input type="checkbox" className="rounded border-gray-300 text-[#ec8301] focus:ring-[#ec8301]" checked={selectedRecords.length === filteredAndSortedRecords.length} onChange={() => toggleSelectAll(filteredAndSortedRecords)} data-id="element-34" />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-35">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-36">
                  Chargen-Nr.
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-37">
                  Produkt
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-38">
                  Datum
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-39">
                  Rezept-Nr.
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-40"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-41">
              {filteredAndSortedRecords.map(record => <Fragment key={record.id} data-id="element-42">
                  <tr className={`hover:bg-gray-50 cursor-pointer ${expandedRows.includes(record.id) ? 'bg-gray-50' : ''}`} data-id="element-43">
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-44">
                      <input type="checkbox" className="rounded border-gray-300 text-[#ec8301] focus:ring-[#ec8301]" checked={selectedRecords.includes(record.id)} onChange={e => toggleSelectRecord(record.id, e)} disabled={record.exported} data-id="element-45" />
                    </td>
                    <td className="px-4 py-3" onClick={() => toggleRow(record.id)} data-id="element-46">
                      {record.exported ? <div className="flex items-center gap-1 text-green-600" data-id="element-47">
                          <CheckIcon className="h-5 w-5" data-id="element-48" />
                          <span className="text-xs" data-id="element-49">
                            {new Date(record.exportedAt!).toLocaleDateString('de-DE')}
                          </span>
                        </div> : <div className="flex items-center gap-1 text-[#ec8301]" data-id="element-50">
                          <AlertTriangleIcon className="h-5 w-5" data-id="element-51" />
                          <span className="text-xs" data-id="element-52">Nicht exportiert</span>
                        </div>}
                    </td>
                    <td className="px-4 py-3 font-mono" onClick={() => toggleRow(record.id)} data-id="element-53">
                      {record.batchNumber}
                    </td>
                    <td className="px-4 py-3" onClick={() => toggleRow(record.id)} data-id="element-54">
                      {record.productName}
                    </td>
                    <td className="px-4 py-3" onClick={() => toggleRow(record.id)} data-id="element-55">
                      {new Date(record.productionDate).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-3 font-mono" onClick={() => toggleRow(record.id)} data-id="element-56">
                      {record.recipe.batchNumber}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={() => toggleRow(record.id)} data-id="element-57">
                      {expandedRows.includes(record.id) ? <ChevronUpIcon className="h-5 w-5 text-gray-400" data-id="element-58" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-59" />}
                    </td>
                  </tr>
                  {expandedRows.includes(record.id) && <tr data-id="element-60">
                      <td colSpan={5} className="px-4 py-4 bg-gray-50" data-id="element-61">
                        <div className="space-y-6" data-id="element-62">
                          {/* Recipe Details */}
                          <div data-id="element-63">
                            <h3 className="text-lg font-medium mb-2" data-id="element-64">
                              Rezeptdetails
                            </h3>
                            <div className="bg-white rounded-lg border border-gray-200" data-id="element-65">
                              <table className="w-full text-sm" data-id="element-66">
                                <thead className="bg-gray-50" data-id="element-67">
                                  <tr data-id="element-68">
                                    <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-69">
                                      Zutat
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-70">
                                      Chargen-Nr.
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-71">
                                      Menge
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200" data-id="element-72">
                                  {record.recipe.ingredients.map((ingredient, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-73">
                                        <td className="px-4 py-2" data-id="element-74">
                                          {ingredient.name}
                                        </td>
                                        <td className="px-4 py-2 font-mono" data-id="element-75">
                                          {ingredient.batchNumber}
                                        </td>
                                        <td className="px-4 py-2" data-id="element-76">
                                          {ingredient.quantity}{' '}
                                          {ingredient.unit}
                                        </td>
                                      </tr>)}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {/* Production Steps */}
                          <div data-id="element-77">
                            <h3 className="text-lg font-medium mb-2" data-id="element-78">
                              Produktionsschritte
                            </h3>
                            <div className="space-y-4" data-id="element-79">
                              {record.productionSteps.map((step, stepIndex) => <div key={stepIndex} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-80">
                                  <div className="flex items-center gap-2 mb-3" data-id="element-81">
                                    {(() => {
                              const Icon = productionStepIcons[step.step];
                              return <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.status === 'completed' ? 'bg-green-500' : step.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-82">
                                          <Icon className="h-4 w-4 text-white" data-id="element-83" />
                                        </div>;
                            })()}
                                    <div data-id="element-84">
                                      <h4 className="font-medium" data-id="element-85">
                                        {productionStepLabels[step.step]}
                                      </h4>
                                      <p className="text-sm text-gray-500" data-id="element-86">
                                        Chargen-Nr. {step.batchNumber}
                                      </p>
                                    </div>
                                  </div>
                                  {step.materials.length > 0 && <div className="mt-3" data-id="element-87">
                                      <table className="w-full text-sm" data-id="element-88">
                                        <thead className="bg-gray-50" data-id="element-89">
                                          <tr data-id="element-90">
                                            <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-91">
                                              Material
                                            </th>
                                            <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-92">
                                              Chargen-Nr.
                                            </th>
                                            <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-93">
                                              Menge
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200" data-id="element-94">
                                          {step.materials.map((material, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-95">
                                                <td className="px-4 py-2" data-id="element-96">
                                                  {material.name}
                                                </td>
                                                <td className="px-4 py-2 font-mono" data-id="element-97">
                                                  {material.batchNumber}
                                                </td>
                                                <td className="px-4 py-2" data-id="element-98">
                                                  {material.quantity}{' '}
                                                  {material.unit}
                                                </td>
                                              </tr>)}
                                        </tbody>
                                      </table>
                                    </div>}
                                  {step.operator && <div className="mt-3 text-sm text-gray-500" data-id="element-99">
                                      <p data-id="element-100">Bearbeiter: {step.operator}</p>
                                      <p data-id="element-101">
                                        Start:{' '}
                                        {new Date(step.startTime!).toLocaleString()}
                                      </p>
                                      {step.completionTime && <p data-id="element-102">
                                          Ende:{' '}
                                          {new Date(step.completionTime).toLocaleString()}
                                        </p>}
                                    </div>}
                                </div>)}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}